import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import Avatar from 'react-avatar';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import ElipsisText from '../../../../components/ElipsisText';
import ElipsisCell from '../../../../components/TableBox/Cells/EllipsisCell';
import Link from '../../../../components/Link';
import Badge from '../../../../components/Badge';
import Button from '../../../../components/Button';
import {
  RowClassName,
  Modules,
  AuthRoutes,
  RouteParamsTypes,
  PermissionModulesEnum,
  PermissionActionsEnum,
} from '../../../../enums';
import { TableContext } from '../../../../contexts/TableContext';
import { AuthContext } from '../../../../contexts/AuthContext';
import { hasResourseAccess } from '../../../../utils/methods';

interface RowProps {
  item: any;
  className?: RowClassName;
}

const Row: React.FC<RowProps> = ({ item, className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { headCells } = useContext(TableContext);
  const { isSystemAdmin, permissions } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.Developers]: [PermissionActionsEnum.Update] }],
  });

  const handleEditButtonClick = (id: string, e?: any) => {
    const url = `${Modules.developers}${AuthRoutes.developers}${RouteParamsTypes.edit}/${id}`;
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };
  const handleViewClick = (id: string, e?: any) => {
    const url = `${Modules.developers}${AuthRoutes.developers}${RouteParamsTypes.view}/${id}`;
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };

  const developerNode = (
    <div className="d-flex align-items-center">
      <div className="me-2" style={{ width: 42 }}>
        <Tooltip
          title={
            item.logo_url ? (
              <img
                src={item.logo_url}
                alt={item.name}
                style={{
                  width: '150px',
                  height: '150px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                }}
              />
            ) : (
              ''
            )
          }
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'transparent',
                p: 0,
              },
            },
          }}
        >
          <div>
            <Avatar
              name={item.name || '-'}
              src={item.logo_url}
              size="40"
              maxInitials={2}
              round
            />
          </div>
        </Tooltip>
      </div>
      <div style={{ width: 'calc(100% - 52px)' }}>
        <ElipsisText
          width="100%"
          text={item.name || '-'}
          node={<Link text={item.name || '-'} />}
          className="h6 mb-1 text-decoration-underline hyperlink"
          onClick={(e) => handleViewClick(item.id, e)}
        />
        <ElipsisText width="100%" text={item.alias_name || '-'} />
      </div>
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.name) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={developerNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.email) {
            return <ElipsisCell key={headCell.id} width={headCell.width} text={item.email || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.phoneNumber) {
             return <ElipsisCell key={headCell.id} width={headCell.width} text={item.phone || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.area_id) {
            return <ElipsisCell key={headCell.id} width={headCell.width} text={item.area?.name || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.addressCity) {
            return <ElipsisCell key={headCell.id} width={headCell.width} text={item.city?.name || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.addressState) {
            return <ElipsisCell key={headCell.id} width={headCell.width} text={item.state?.name || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.addressCountry) {
            return <ElipsisCell key={headCell.id} width={headCell.width} text={item.country?.name || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Badge
                text={
                  item.is_active
                    ? t('developers.list.pageTexts.table.status.active')
                    : t('developers.list.pageTexts.table.status.inActive')
                }
                variant={item.is_active ? 'success' : 'danger'}
              />
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex align-items-sm-center justify-content-sm-start">
                {hasEditAccess ? (
                  <Button
                    className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                    icon={<i className="ri-edit-box-line ri-20px"></i>}
                    onClick={(e) => handleEditButtonClick(item.id, e)}
                  />
                ) : (
                  <Button
                    className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                    icon={<i className="ri-eye-line ri-20px"></i>}
                    onClick={(e) => handleViewClick(item.id, e)}
                  />
                )}
                 <Tooltip
                  title={
                    <>
                      <div>
                        {t('common.table.lastUpdatedBy')} {item.updater?.name || '-'}
                      </div>
                      <div>
                        {t('common.table.lastUpdated')} {item.updated_at || '-'}
                      </div>
                    </>
                  }
                  arrow
                  classes={{
                    tooltip: 'tooltip',
                  }}
                >
                  <i className="ri-timer-line ri-20px"></i>
                </Tooltip>
              </div>
            </td>
          );
        }

        return (
          <ElipsisCell key={headCell.id} width={headCell.width} text={item[headCell.id] || '-'} />
        );
      })}
    </tr>
  );
};

export default Row;
